import { forwardRef, useMemo, useState } from 'react'
import { Breadcrumbs, type BreadcrumbItem } from '../Breadcrumbs'
import { EChart, type EChartHandle } from './EChart'
import { buildTreemapOption, nodeAtPath, type TreemapColorBy, type TreemapNode } from './treemapOption'
import { useChartTheme } from './useChartTheme'

export interface TreemapDrillProps {
  /** Узлы верхнего уровня; дети, если есть, раскрываются кликом на клиенте. */
  root: TreemapNode[]
  /** Путь id от корня (управляемый режим — синхронизация с крошками страницы и URL). */
  path?: string[]
  onPathChange?: (path: string[]) => void
  /**
   * Клик по узлу с `hasChildren` без загруженных детей (ленивый провал: страница грузит уровень сама и
   * передаёт его как новый `root`). Если задан — вызывается для любого узла с уровнем ниже.
   */
  onNodeClick?: (node: TreemapNode) => void
  /** Клик по листу (уровня ниже нет): например, открыть карточку артикула. */
  onLeafClick?: (node: TreemapNode) => void
  /** Подпись корня в крошках. */
  rootLabel?: string
  /** Крошки страницы вместо встроенных (ленивый режим): одни и те же для графика и страницы. */
  crumbs?: BreadcrumbItem[]
  colorBy?: TreemapColorBy
  deltaLimit?: number
  valueFormat?: (value: number) => string
  invertDelta?: boolean
  height?: number
  loading?: boolean
  ariaLabel?: string
  className?: string
  texture?: boolean
}

/**
 * Структура «категория → тема → макет → артикул»: прямоугольники долей текущего уровня, провал по клику,
 * крошки над графиком. Уровень — управляемое состояние (`path`), поэтому крошки графика и страницы совпадают.
 */
export const TreemapDrill = forwardRef<EChartHandle, TreemapDrillProps>(function TreemapDrill(
  {
    root,
    path: controlledPath,
    onPathChange,
    onNodeClick,
    onLeafClick,
    rootLabel = 'Все',
    crumbs,
    colorBy,
    deltaLimit,
    valueFormat,
    invertDelta,
    height = 360,
    loading,
    ariaLabel,
    className,
    texture,
  },
  ref,
) {
  const theme = useChartTheme()
  const [ownPath, setOwnPath] = useState<string[]>([])
  const path = controlledPath ?? ownPath
  const setPath = (next: string[]) => {
    onPathChange?.(next)
    if (controlledPath === undefined) setOwnPath(next)
  }

  const { nodes, trail } = useMemo(() => nodeAtPath(root, path), [root, path])
  const option = useMemo(
    () => buildTreemapOption({ nodes, colorBy, deltaLimit, valueFormat, invertDelta }, theme),
    [nodes, colorBy, deltaLimit, valueFormat, invertDelta, theme],
  )

  const breadcrumbs: BreadcrumbItem[] =
    crumbs ??
    (trail.length > 0
      ? [
          { label: rootLabel, onClick: () => setPath([]) },
          ...trail.map((node, index) => ({
            label: node.name,
            onClick: index < trail.length - 1 ? () => setPath(trail.slice(0, index + 1).map((item) => item.id)) : undefined,
          })),
        ]
      : [])

  return (
    <div className={className}>
      {breadcrumbs.length > 0 && <Breadcrumbs showHome={false} items={breadcrumbs} className="mb-3" />}
      <EChart
        ref={ref}
        option={option}
        height={height}
        loading={loading}
        ariaLabel={ariaLabel}
        texture={texture}
        onMarkClick={(params) => {
          if (params.componentType !== 'series') return
          const id = params.data?.id
          const node = nodes.find((item) => item.id === id)
          if (!node) return
          const hasLoadedChildren = Boolean(node.children && node.children.length > 0)
          if (onNodeClick && (node.hasChildren || hasLoadedChildren)) {
            onNodeClick(node)
            return
          }
          if (hasLoadedChildren) {
            setPath([...trail.map((item) => item.id), node.id])
            return
          }
          onLeafClick?.(node)
        }}
      />
    </div>
  )
})

export default TreemapDrill
